/**
 * The real "New chat" creation dialog: a required name field, a real
 * chat-photo upload, an exact-username fan search that needs no shared pool
 * (ISSUE-760's `/profile/search-by-username`), and a working `Create chat`
 * against `/chat/create-standalone`.
 *
 * A NAME IS THE ONLY REQUIREMENT (ISSUE-945). `Create chat` used to stay
 * disabled until at least one other fan had been added, mirroring a server
 * refusal that has since been removed as the request-shape check it always
 * was. The creator can now name a chat, create it, and invite people into it
 * afterwards through the chat's own `Invite people`. The Add people section
 * below is unchanged apart from the line under it, which now says what
 * skipping it means instead of explaining a dead button.
 */
import { useChatCreateStandalone } from "@sportsmet/messaging-sdk/private";
import { useProfileSearchByUsername } from "@sportsmet/profile-sdk/private";
import { Button, Dialog, Input, Skeleton } from "@sportsmet/ui";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";

import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import {
  normalizeUsername,
  PHOTO_ADD_LABEL,
  PHOTO_CHANGE_LABEL,
  PHOTO_UPLOADING_LABEL,
  USERNAME_PATTERN,
} from "../editProfile/editProfileLogic";
import { ChatPhotoField } from "./ChatPhotoField";
import {
  addSelectedMember,
  removeSelectedMember,
  resolveAddPeopleRowAction,
  type typeChatMemberCandidate,
} from "./newChatAddPeople";
import {
  NEW_CHAT_ADD_PEOPLE_ACTION_LABEL,
  NEW_CHAT_ADD_PEOPLE_ADDED_LABEL,
  NEW_CHAT_ADD_PEOPLE_LABEL,
  NEW_CHAT_ADD_PEOPLE_NOT_FOUND_MESSAGE,
  NEW_CHAT_ADD_PEOPLE_OPTIONAL_MESSAGE,
  NEW_CHAT_ADD_PEOPLE_REMOVE_LABEL,
  NEW_CHAT_ADD_PEOPLE_SEARCH_LABEL,
  NEW_CHAT_ADD_PEOPLE_SEARCHING_LABEL,
  NEW_CHAT_ADD_PEOPLE_START_MESSAGE,
  NEW_CHAT_NAME_LABEL,
  NEW_CHAT_NAME_PLACEHOLDER,
  NEW_CHAT_PHOTO_PENDING_MESSAGE,
  NEW_CHAT_SUBMIT_LABEL,
  NEW_CHAT_TITLE,
} from "./standaloneChatCopy";
import { useChatPhotoUpload } from "./useChatPhotoUpload";

type typeAddPeoplePhase = "idle" | "searching" | "found" | "notFound";

/** The `?dialog=` value that opens this dialog on `/messages`. */
export const NEW_CHAT_DIALOG = "new-chat";

export function NewChatDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (chatId: string) => void;
}): ReactNode {
  const [name, setName] = useState("");
  const [usernameQuery, setUsernameQuery] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<
    readonly typeChatMemberCandidate[]
  >([]);
  const queryClient = useQueryClient();
  const createChat = useChatCreateStandalone();
  const {
    state: photoState,
    uploadPhoto,
    reset: resetPhoto,
  } = useChatPhotoUpload();

  const trimmedName = name.trim();

  // The search is exact-match only server side (Q-760-A.1): a fan must type
  // the whole handle before anything returns. Gating the query itself on a
  // complete, validly formatted username means a partial handle never fires
  // a request at all, not only that it comes back empty.
  const normalizedUsernameQuery = normalizeUsername(usernameQuery);
  const isUsernameQueryComplete = USERNAME_PATTERN.test(
    normalizedUsernameQuery,
  );

  const search = useProfileSearchByUsername(
    { username: normalizedUsernameQuery },
    { enabled: open && isUsernameQueryComplete },
  );

  // A failed search (a peer outage, or an offline device) reads differently
  // from a completed search that simply found nobody: it raises the shared
  // offline modal instead of the API error modal.
  useEffect(() => {
    if (!isUsernameQueryComplete) return;
    if (search.isError) {
      showOfflineError();
      return;
    }
    const code = extractErrorCode(search.data);
    if (code !== null) {
      showApiError(search.data, undefined, search.dataUpdatedAt);
    }
  }, [
    isUsernameQueryComplete,
    normalizedUsernameQuery,
    search.isError,
    search.dataUpdatedAt,
  ]);

  const memberUserIds = selectedMembers.map((member) => member.userId);
  // The name is the whole gate. An empty selection creates a chat with one
  // member, which /chat/create-standalone accepts (ISSUE-945).
  const isSubmitDisabled = trimmedName === "" || createChat.isPending;

  let addPeoplePhase: typeAddPeoplePhase;
  if (!isUsernameQueryComplete) {
    addPeoplePhase = "idle";
  } else if (search.isError || extractErrorCode(search.data) !== null) {
    // A miss, a non-discoverable fan, and a blocked pair all answer with the
    // identical empty result server side (Q-760-A.4), and a peer outage
    // fails the query itself. Both read here as "keep typing or try again",
    // never as a hint that a specific reason kept a fan from being found.
    addPeoplePhase = "idle";
  } else if (search.isLoading) {
    addPeoplePhase = "searching";
  } else if ((search.data?.data?.items.length ?? 0) > 0) {
    addPeoplePhase = "found";
  } else {
    addPeoplePhase = "notFound";
  }

  const matchedHit = search.data?.data?.items[0];
  const matchedCandidate: typeChatMemberCandidate | null =
    matchedHit === undefined
      ? null
      : {
          userId: matchedHit.userId,
          username: matchedHit.username,
          displayName: matchedHit.displayName,
          avatarUrl: matchedHit.avatarUrl ?? null,
        };

  // Closing the dialog drops the draft. Reopening "New chat" and finding the
  // last abandoned name, search, or added people still in place reads as a
  // bug, and the create flow is short enough that there is nothing worth
  // preserving.
  const handleOpenChange = (next: boolean): void => {
    if (!next) {
      setName("");
      setUsernameQuery("");
      setSelectedMembers([]);
      resetPhoto();
    }
    onOpenChange(next);
  };

  const handleSubmit = async (): Promise<void> => {
    const photoMediaId =
      photoState.kind === "ready" ? photoState.mediaId : null;
    const result = await createChat.mutateAsync({
      requestId: crypto.randomUUID(),
      title: trimmedName,
      memberUserIds,
      ...(photoMediaId !== null ? { photoMediaId } : {}),
    });
    if (result.error || !result.data) {
      showApiError(result, "chatCreate");
      return;
    }
    void queryClient.invalidateQueries();
    handleOpenChange(false);
    onCreated(result.data.chatId);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title={NEW_CHAT_TITLE}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              handleOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            data-testid="new-chat-submit"
            disabled={isSubmitDisabled}
            loading={createChat.isPending}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {NEW_CHAT_SUBMIT_LABEL}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <ChatPhotoField
          title={trimmedName}
          state={photoState}
          onSelectPhoto={uploadPhoto}
          testId="new-chat-photo"
          addLabel={PHOTO_ADD_LABEL}
          changeLabel={PHOTO_CHANGE_LABEL}
          uploadingLabel={PHOTO_UPLOADING_LABEL}
          pendingMessage={NEW_CHAT_PHOTO_PENDING_MESSAGE}
        />

        <Input
          label={NEW_CHAT_NAME_LABEL}
          placeholder={NEW_CHAT_NAME_PLACEHOLDER}
          required
          data-testid="new-chat-name"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />

        <div className="flex flex-col gap-2">
          <p className="text-body-strong text-ink">
            {NEW_CHAT_ADD_PEOPLE_LABEL}
          </p>
          <Input
            label={NEW_CHAT_ADD_PEOPLE_SEARCH_LABEL}
            placeholder="username"
            data-testid="new-chat-add-people-search"
            value={usernameQuery}
            onChange={(event) => {
              setUsernameQuery(event.target.value);
            }}
          />
          {addPeoplePhase === "idle" ? (
            <p
              className="text-body text-ink-secondary"
              data-testid="new-chat-add-people-note"
            >
              {NEW_CHAT_ADD_PEOPLE_START_MESSAGE}
            </p>
          ) : null}
          {addPeoplePhase === "searching" ? (
            <div data-testid="new-chat-add-people-searching">
              <Skeleton
                shape="control"
                label={NEW_CHAT_ADD_PEOPLE_SEARCHING_LABEL}
              />
            </div>
          ) : null}
          {addPeoplePhase === "notFound" ? (
            <p
              className="text-body text-ink-secondary"
              data-testid="new-chat-add-people-empty"
            >
              {NEW_CHAT_ADD_PEOPLE_NOT_FOUND_MESSAGE}
            </p>
          ) : null}
          {addPeoplePhase === "found" && matchedCandidate !== null ? (
            <div
              className="flex min-w-0 items-center gap-3"
              data-testid={`new-chat-add-people-result-${matchedCandidate.userId}`}
            >
              {matchedCandidate.avatarUrl !== null ? (
                <img
                  src={matchedCandidate.avatarUrl}
                  alt=""
                  className="h-icon-lg w-icon-lg"
                />
              ) : null}
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="text-body-strong text-ink break-all">
                  {matchedCandidate.displayName}
                </span>
                <span className="text-caption text-ink-secondary break-all">
                  @{matchedCandidate.username}
                </span>
              </div>
              {resolveAddPeopleRowAction(matchedCandidate, selectedMembers) ===
              "add" ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="ml-auto"
                  data-testid={`new-chat-add-people-action-${matchedCandidate.userId}`}
                  onClick={() => {
                    setSelectedMembers((prev) =>
                      addSelectedMember(prev, matchedCandidate),
                    );
                  }}
                >
                  {NEW_CHAT_ADD_PEOPLE_ACTION_LABEL}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled
                  className="ml-auto"
                  data-testid={`new-chat-add-people-action-${matchedCandidate.userId}`}
                >
                  {NEW_CHAT_ADD_PEOPLE_ADDED_LABEL}
                </Button>
              )}
            </div>
          ) : null}

          {selectedMembers.length > 0 ? (
            <ul
              className="flex flex-col gap-2"
              data-testid="new-chat-selected-members"
            >
              {selectedMembers.map((member) => (
                <li
                  key={member.userId}
                  className="flex min-w-0 items-center gap-3"
                >
                  <span className="min-w-0 flex-1 text-body text-ink break-all">
                    {member.displayName}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    data-testid={`new-chat-remove-member-${member.userId}`}
                    onClick={() => {
                      setSelectedMembers((prev) =>
                        removeSelectedMember(prev, member.userId),
                      );
                    }}
                  >
                    {NEW_CHAT_ADD_PEOPLE_REMOVE_LABEL}
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}

          {memberUserIds.length === 0 ? (
            <p
              className="text-caption text-ink-secondary"
              data-testid="new-chat-add-people-optional"
            >
              {NEW_CHAT_ADD_PEOPLE_OPTIONAL_MESSAGE}
            </p>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}
